import { FC, useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { NsKey } from 'enums/NsKey';
import { usePanel } from 'hooks/usePanel';
import { requestQuoteValidation } from '../../api/quoteValidation';

const MESSAGE_MAX_LENGTH = 2000;

interface RequestValidationPanelProps {
  devisId: string;
  /** Reloads the contractualization data once the request is sent. */
  onSuccess: () => void;
}

/**
 * Panel sending the quote to the tunnel validators, with an optional message.
 * The number of recipients is only known once the request is sent: it is not shown here.
 */
export const RequestValidationPanel: FC<RequestValidationPanelProps> = ({ devisId, onSuccess }) => {
  const { t } = useTranslation(NsKey.Contractualization);
  const { setPanelActions, closePanel } = usePanel();
  const [message, setMessage] = useState('');
  const [isSending, setIsSending] = useState(false);

  const handleSubmit = useCallback(async () => {
    setIsSending(true);
    try {
      const res = await requestQuoteValidation(devisId, message);
      if (res.isSuccess) {
        // Success toaster shown automatically from OperationResponse.notification.
        onSuccess();
        closePanel();
      } else {
        toast.error(res.errorMessage || t('CONTRACTUALIZATION.CARDS.VALIDATION_REQUEST_PANEL.ERROR'));
      }
    } catch (error) {
      console.error('Error requesting the quote validation:', error);
      toast.error(t('CONTRACTUALIZATION.CARDS.VALIDATION_REQUEST_PANEL.ERROR'));
    } finally {
      setIsSending(false);
    }
  }, [devisId, message, onSuccess, closePanel, t]);

  useEffect(() => {
    setPanelActions({
      closeLabel: 'COMMON.CANCEL',
      submitLabel: t('CONTRACTUALIZATION.CARDS.VALIDATION_REQUEST_PANEL.SUBMIT'),
      submitDisabled: isSending,
      onSubmit: handleSubmit
    });
  }, [setPanelActions, handleSubmit, isSending, t]);

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-gray-600">
        {t('CONTRACTUALIZATION.CARDS.VALIDATION_REQUEST_PANEL.INTRO')}
      </p>
      <label className="flex flex-col gap-2">
        <span className="text-sm font-medium text-gray-700">
          {t('CONTRACTUALIZATION.CARDS.VALIDATION_REQUEST_PANEL.MESSAGE_LABEL')}
        </span>
        <textarea
          className="min-h-32 w-full rounded-lg border border-gray-300 p-3 text-sm focus:border-primary focus:outline-none"
          value={message}
          maxLength={MESSAGE_MAX_LENGTH}
          placeholder={t('CONTRACTUALIZATION.CARDS.VALIDATION_REQUEST_PANEL.MESSAGE_PLACEHOLDER')}
          onChange={(e) => setMessage(e.target.value)}
          disabled={isSending}
        />
        <span className="self-end text-xs text-gray-400">
          {message.length} / {MESSAGE_MAX_LENGTH}
        </span>
      </label>
    </div>
  );
};
